/**
 * EPBCS Setup — Driver Based Budgeting Setup.
 * Lets an admin define the calculation formula (using Driver Master codes) for every
 * Revenue/Expense account whose Budget Method is "driver".
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import { Field, Input, Select, Card, Btn } from "./common";

const MEASURES = ["Units", "Amount", "Percentage", "Rate", "Count"];

export default function DriverSetupTab({ isAdmin }) {
  const [data, setData] = useState(null);
  const [formulas, setFormulas] = useState({});
  const [newDriver, setNewDriver] = useState(null);
  const [savingAcct, setSavingAcct] = useState("");

  const load = () => api.get("/epbcs/driver-setup").then((r) => {
    setData(r.data);
    const f = {};
    r.data.accounts.forEach((a) => { f[a.code] = a.attrs?.driver_formula || ""; });
    setFormulas(f);
  }).catch((e) => toast.error(e?.response?.data?.detail || "Could not load driver setup"));
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (!data) return <p className="text-sm text-slate-400">Loading…</p>;

  const insert = (code, driverCode) => {
    setFormulas((f) => ({ ...f, [code]: `${f[code] || ""}${f[code] ? " * " : ""}${driverCode}` }));
  };

  const saveFormula = async (acct) => {
    setSavingAcct(acct.code);
    try {
      await api.post("/epbcs/dimensions", {
        id: acct.id, dim_type: "account", code: acct.code, name: acct.name,
        attrs: { ...acct.attrs, driver_formula: formulas[acct.code] || "" },
      });
      toast.success(`Driver formula saved for ${acct.code}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save formula");
    } finally {
      setSavingAcct("");
    }
  };

  const createDriver = async () => {
    try {
      await api.post("/epbcs/dimensions", {
        dim_type: "driver", code: newDriver.code, name: newDriver.name,
        attrs: { measure: newDriver.measure },
      });
      toast.success(`Driver ${newDriver.code} created`);
      setNewDriver(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create driver");
    }
  };

  return (
    <div className="space-y-5" data-testid="driver-setup-tab">
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold tracking-tight">Driver Master</h3>
            <p className="text-[12px] text-slate-500">
              Drivers used to build formulas for Driver Based Revenue &amp; Expense accounts.
            </p>
          </div>
          {isAdmin && !newDriver && (
            <button onClick={() => setNewDriver({ code: "", name: "", measure: MEASURES[0] })}
              data-testid="new-driver-btn"
              className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
              <Plus size={13} weight="bold" /> New driver
            </button>
          )}
        </div>
        {newDriver && (
          <div className="rounded-sm border border-[#0B1E3F]/30 bg-slate-50 p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="new-driver-draft">
            <Field label="Code *"><Input data-testid="driver-code" value={newDriver.code}
              onChange={(e) => setNewDriver({ ...newDriver, code: e.target.value.toUpperCase() })} /></Field>
            <Field label="Name *"><Input data-testid="driver-name" value={newDriver.name}
              onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} /></Field>
            <Field label="Measure *">
              <Select data-testid="driver-measure" value={newDriver.measure}
                onChange={(e) => setNewDriver({ ...newDriver, measure: e.target.value })}>
                {MEASURES.map((m) => <option key={m} value={m}>{m}</option>)}
              </Select>
            </Field>
            <div className="flex items-end gap-2">
              <button onClick={() => setNewDriver(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">Cancel</button>
              <Btn onClick={createDriver} disabled={!newDriver.code || !newDriver.name} data-testid="driver-save">Create</Btn>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {data.drivers.map((d) => (
            <span key={d.code} className="text-[11px] bg-slate-100 border border-slate-200 rounded px-2 py-1"
              data-testid={`driver-chip-${d.code}`}>
              <b>{d.code}</b> — {d.name} <span className="text-slate-400">({d.attrs?.measure || "—"})</span>
            </span>
          ))}
          {data.drivers.length === 0 && <p className="text-[12px] text-slate-400">No drivers yet — create one above.</p>}
        </div>
      </Card>

      <Card>
        <h3 className="font-bold tracking-tight mb-1">Driver Based Budgeting Setup</h3>
        <p className="text-[12px] text-slate-500 mb-4">
          One row per account with Budget Method = "driver". Build the formula using driver codes
          (e.g. <code>DRV01 * DRV02</code>) — click a driver chip below to insert its code.
        </p>
        <div className="space-y-4">
          {data.accounts.map((a) => (
            <div key={a.code} className="border border-slate-200 rounded-sm p-3" data-testid={`driver-account-${a.code}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-bold">{a.code} — {a.name}
                  <span className="ml-2 text-[10px] font-normal uppercase tracking-wider text-slate-400">{a.attrs?.account_type}</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Input value={formulas[a.code] || ""} data-testid={`driver-formula-${a.code}`}
                  placeholder="e.g. DRV01 * DRV02"
                  onChange={(e) => setFormulas((f) => ({ ...f, [a.code]: e.target.value }))} />
                <Btn onClick={() => saveFormula(a)} disabled={savingAcct === a.code} data-testid={`driver-formula-save-${a.code}`}>
                  Save
                </Btn>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {data.drivers.map((d) => (
                  <button key={d.code} onClick={() => insert(a.code, d.code)}
                    data-testid={`driver-insert-${a.code}-${d.code}`}
                    className="text-[10px] bg-white border border-slate-200 hover:border-[#0B1E3F] rounded px-1.5 py-0.5">
                    + {d.code}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {data.accounts.length === 0 && (
            <p className="text-[12px] text-slate-400">
              No accounts with Budget Method = "driver" yet — set that on an account under Dimension Masters &gt; Accounts.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
